import {
    bindActionCreators,
    createListenerMiddleware,
    isAnyOf,
} from '@reduxjs/toolkit';
import {
    AppDispatch,
    RootState,
} from '../../store';
import {
    getData,
    goToBasket,
    selectActiveSubscriptionId,
    selectCustomerConfig,
    selectIsEligibleForRestlaufzeit,
    selectPreviousHardwareConfig,
    setGigakombi,
    setInitialState,
    setRestlaufzeit,
    setSalesChannel,
    setCashback,
    setSubscription,
    skipConnectMore,
    toggleDevice,
    toggleRedPlusId,
} from 'Container/App/slice';
import {
    RedplusTariff,
    RedTariff,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_ATOMIC_ID,
    SAILS_PARAM_DEVICE_ID,
    SAILS_PARAM_FLOW_TYPE,
    SAILS_PARAM_GIGAKOMBI,
    SAILS_PARAM_HARDWAREONLY,
    SAILS_PARAM_INSURANCE,
    SAILS_PARAM_RED_PLUS_IDS,
    SAILS_PARAM_RLZ,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SAILS_PARAM_TAUSCHBONUS,
    SAILS_PARAM_TRADE_IN,
    SALESCHANNEL_REDPLUS,
    URL_PARAM_ATOMIC_ID,
    URL_PARAM_SUBGROUP_ID,
    YoungTariff,
} from '@vfde-sails/constants';
import { getAllDiscounts } from '@vfde-sails/page-options';
import {
    selectAvailableOffers,
    selectCurrentOfferData,
    selectTrackingPayload,
} from 'Container/App/selectors';
import {
    getSessionStorageItemJson,
    updateStorage,
} from '@vfde-sails/storage';
import { SailsTariffOverviewStorage } from 'Container/App/interface';
import getPageUrlForCustomerConfig from 'Container/App/helpers/getPageUrlForCustomerConfig';
import NAMESPACE from 'Constant';
import { findEquivalentTariff } from 'Container/App/helpers/findEquivalentTariff';
import {
    createPageViewTrackingData,
    createPageViewTrackingDataWithError,
} from 'Container/App/helpers/tracking';
import {
    TrackingError,
    TrackingErrorType,
    trackIt,
    TrackType,
} from '@vfde-sails/tracking';
import {
    BASKET_PROPOSITION_BNT,
    BASKET_PROPOSITION_REDPLUS,
    createBasket,
    createBasketItem,
    createBasketProposition,
    createSailsReadyEvent,
    generateDeeplink,
    hasQueryParam,
    isShallowEqual,
    removeQueryParam,
    updateUrl,
} from '@vfde-sails/utils';
import { AbstractedTariffWithHardwareOffer } from '@vfde-sails/glados-v2';

const appListener = createListenerMiddleware();

export default appListener;

export const startAppListening = appListener.startListening.withTypes<
    RootState,
    AppDispatch
>();

export const listeners = {
    setInitialStateFulfilled: () => startAppListening({
        actionCreator: setInitialState.fulfilled,
        effect: (_action, { dispatch }) => {
            dispatch(getData());
        },
    }),
    setInitialStateRejected: () => startAppListening({
        actionCreator: setInitialState.rejected,
        effect: action => {
            action.payload && window.location.replace(action.payload);
        },
    }),
    /**
     * Update storage after customer config changed
     * ATTENTION: It's important that this listener comes before the listener for the GK/sales channel toggle redirect
     * because otherwise it updates the storage too late.
     * @todo maybe this can be solved more robust with RTK listener tools like delay and fork?
     */
    customerConfigChanged: () => startAppListening({
        predicate: (_action, currentState, originalState) =>
            JSON.stringify(selectCustomerConfig(currentState)) !== JSON.stringify(selectCustomerConfig(originalState)),
        effect: (_action, api) => {
            const [
                {
                    deviceId,
                    atomicId,
                    hasGigakombi,
                    insurance,
                    hasTauschbonus,
                    hasTradeIn,
                    salesChannel,
                    flowType,
                    isRestlaufzeit,
                    redplusIds,
                },
                {
                    salesChannel: oldSalesChannel,
                },
                activeSubscriptionId,
                previousHardwareConfig,
                oldStorage,
            ] = [
                selectCustomerConfig(api.getState()),
                selectCustomerConfig(api.getOriginalState()),
                selectActiveSubscriptionId(api.getState()),
                selectPreviousHardwareConfig(api.getState()),
                getSessionStorageItemJson<SailsTariffOverviewStorage>(SAILS_BNT_STORAGE),
            ];

            let salesChannelForStorage = salesChannel;

            if (salesChannel !== oldSalesChannel) {
                // Sales channel was toggled - we have to use the old saleschannel for updating the activeSubscription storage (CO-38650)
                // otherwise a young tariff is written in the consumer storage and vice versa
                salesChannelForStorage = oldSalesChannel;
            }

            updateStorage<SailsTariffOverviewStorage>(SAILS_BNT_STORAGE, {
                [SAILS_PARAM_SALESCHANNEL]: salesChannel!,
                [SAILS_PARAM_SUB_ID]: {
                    ...oldStorage?.[SAILS_PARAM_SUB_ID],
                    [salesChannelForStorage]: activeSubscriptionId,
                },
                [SAILS_PARAM_ATOMIC_ID]: atomicId || undefined,
                [SAILS_PARAM_DEVICE_ID]: deviceId || undefined,
                [SAILS_PARAM_GIGAKOMBI]: hasGigakombi,
                [SAILS_PARAM_INSURANCE]: insurance,
                [SAILS_PARAM_TRADE_IN]: !!atomicId && hasTradeIn,
                [SAILS_PARAM_TAUSCHBONUS]: !!atomicId && hasTauschbonus,
                [SAILS_PARAM_FLOW_TYPE]: flowType,
                [SAILS_PARAM_HARDWAREONLY]: false, // If hardwareonly is true from a DDP visit, set it to false here (See INCM-801)
                [SAILS_PARAM_RLZ]: isRestlaufzeit,
                [SAILS_PARAM_RED_PLUS_IDS]: redplusIds as RedplusTariff[],
                [NAMESPACE]: {
                    previousHardware: previousHardwareConfig || undefined,
                    previousOffer: oldStorage?.[NAMESPACE]?.previousOffer,
                    restoreFocus: oldStorage?.[NAMESPACE]?.restoreFocus,
                },
            }, { shouldDeepMerge: false });
        },
    }),
    setGigaKombiOrSalesChannel: () => startAppListening({
        matcher: isAnyOf(setGigakombi, setSalesChannel),
        effect: (action, { getState, getOriginalState }) => {
            const customerConfig = selectCustomerConfig(getState());
            // We need to select the current offer from the original state, because changing gigakombi when RLZ is active causes
            // an empty query state because of the config change (combination of RLZ setting and GK is relevant for RTK query).
            const currentOffer = selectCurrentOfferData(getOriginalState());
            const storage = getSessionStorageItemJson<SailsTariffOverviewStorage>(SAILS_BNT_STORAGE);

            const pageUrl = getPageUrlForCustomerConfig(customerConfig.salesChannel!, customerConfig!.hasGigakombi);

            // If we are already on the correct page, don't redirect
            if (window.location.href.includes(pageUrl)) {
                return;
            }

            updateStorage<SailsTariffOverviewStorage>(SAILS_BNT_STORAGE, {
                [NAMESPACE]: {
                    // Only write backup in storage in case user didn't toggle sales channel, or they
                    // don't have a selection for the selected sales channel yet (CO-38650)
                    ...(!setSalesChannel.match(action) || (!storage?.[SAILS_PARAM_SUB_ID]?.[action.payload])) && {
                        previousOffer: {
                            tariffId: currentOffer!.tariffId,
                            dataVolumeWithDiscounts: currentOffer!.dataVolumeWithDiscounts,
                            dataVolumeWithoutDiscounts: currentOffer!.dataVolumeWithoutDiscounts,
                            priceMonthlyTariffWithDiscounts: currentOffer!.priceMonthlyTariffWithDiscounts,
                            priceMonthlyTariffWithoutDiscounts: currentOffer!.priceMonthlyTariffWithoutDiscounts,
                        },
                    },
                    restoreFocus: {
                        [action.type]: `${action.payload}`,
                    },
                },
            }, storage, { shouldDeepMerge: true });

            window.location.assign(pageUrl);
        },
    }),
    getDataFulfilledInitial: () => startAppListening({
        actionCreator: getData.fulfilled,
        effect: (_action, api) => {
            // unsubscribe immediately because we only want to expose the public functions and track the initial pageview once
            api.unsubscribe();

            const actions = bindActionCreators({
                goToBasket,
                skipConnectMore,
                toggleRedPlusId,
                setSubscription,
                setGigakombi,
                setCashback,
            }, api.dispatch);

            // Expose public functions
            createSailsReadyEvent([
                {
                    actions: {
                        goToBasket: actions.goToBasket,
                    },
                },
                {
                    actions: {
                        skipConnectMore: actions.skipConnectMore,
                        // eslint-disable-next-line jsdoc/require-jsdoc
                        update: ({ subscriptionId, isGigakombi }: { subscriptionId?: RedTariff | YoungTariff; isGigakombi?: boolean}) => {
                            subscriptionId && actions.setSubscription(subscriptionId);
                            isGigakombi !== undefined && actions.setGigakombi(isGigakombi);
                        },
                        addRedPlusIds: (id: string) => actions.toggleRedPlusId(id, true),
                        setCashback: actions.setCashback,
                        removeRedPlusIds: (id: string) => actions.toggleRedPlusId(id, false),
                    },
                    applicationScope: NAMESPACE,
                },
            ]);

            const state = api.getState();
            const customerConfig = selectCustomerConfig(state);
            const { selectedSubscriptionId, atomicId } = customerConfig;

            // If we already have a subscription selected, or we don't have an atomic, we can track initial page load.
            // Otherwise, tracking will be triggered by the listener 'currentOfferChanged'.
            if (selectedSubscriptionId || !atomicId) {
                // Track initial page view
                const trackingData = selectTrackingPayload(state);
                const trackingPayload = createPageViewTrackingData(trackingData);

                trackingPayload && trackIt(TrackType.PageView, trackingPayload);
            }
        },
    }),
    getDataFulfilled: () => startAppListening({
        actionCreator: getData.fulfilled,
        effect: (_action, api) => {
            const state = api.getState();
            const customerConfig = selectCustomerConfig(state);
            const availableOffers = selectAvailableOffers(state);
            const storage = getSessionStorageItemJson<SailsTariffOverviewStorage>(SAILS_BNT_STORAGE);

            if (storage?.[NAMESPACE]?.previousOffer && availableOffers.length) {
                const backupOfActiveSubscription = storage[NAMESPACE].previousOffer;

                // Remove backup from storage, because it's no longer needed and we don't want this function to be executed more than once
                updateStorage<SailsTariffOverviewStorage>(SAILS_BNT_STORAGE, {
                    [NAMESPACE]: {
                        previousOffer: undefined,
                        restoreFocus: storage[NAMESPACE].restoreFocus,
                    },
                }, storage, { shouldDeepMerge: false });

                let newSubscriptionId = availableOffers
                    .find(subscription => subscription.tariffId === backupOfActiveSubscription.tariffId)?.tariffId;

                if (!newSubscriptionId) {
                    newSubscriptionId =
                        findEquivalentTariff(storage[NAMESPACE].previousOffer, availableOffers);
                }

                api.dispatch(setSubscription(newSubscriptionId));
            }

            if (customerConfig.atomicId && !customerConfig.selectedSubscriptionId) {
                api.dispatch(setSubscription(
                    availableOffers
                        .find(subscription => (subscription as AbstractedTariffWithHardwareOffer).isCheapest)?.tariffId || null,
                ));
            }

            // DO NOT EVER add page view tracking to this listener.
            // It's triggered not only on page load but on several toggling actions as well
            // which are not supposed to trigger a page view.
            // The listener 'getDataFulfilledInitial' handles tracking.
        },
    }),
    getDataRejected: () => startAppListening({
        actionCreator: getData.rejected,
        effect: (action, { cancelActiveListeners, getState }) => {
            cancelActiveListeners();

            const trackingData = selectTrackingPayload(getState());
            const trackingError: TrackingError = {
                /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                error_type: TrackingErrorType.Technical,
                error_message: action.payload!.message,
                error_code: action.payload!.status?.toString(),
                error_location: 'getData',
                /* eslint-enable camelcase, @typescript-eslint/naming-convention */
            };

            trackIt(
                TrackType.PageView,
                createPageViewTrackingDataWithError(trackingData, trackingError),
            );
        },
    }),
    goToBasket: () => startAppListening({
        actionCreator: goToBasket,
        effect: async (action, api) => {
            const customerConfig = selectCustomerConfig(api.getState());
            const isEligibleForRestlaufzeit = selectIsEligibleForRestlaufzeit(api.getState());

            const { hasGigakombi: hadGigakombi } = selectCustomerConfig(api.getOriginalState());

            // If `payload` is not undefined, then the user triggered this action via the gigakombi overlay. If
            // `payload` and `hadGigakombi` are not equal, then they toggled their gigakombi setting. We have to update
            // the history, so the user goes back to the correct page in the flow on browser back button.
            if (action.payload !== undefined && hadGigakombi !== action.payload) {
                const url = getPageUrlForCustomerConfig(customerConfig.salesChannel, action.payload);

                updateUrl(url, true);

                // We also have to request the API again in case Restlaufzeit was true,
                // to prevent an edge case happening with browser back (see CO-38919).
                if (customerConfig.isRestlaufzeit) {
                    await api.dispatch(getData());
                }
            }

            const currentOffer = selectCurrentOfferData(api.getState());

            const serviceIds = [];
            customerConfig.insurance && serviceIds.push(customerConfig.insurance);

            const redPlusItems = [];

            if (customerConfig.redplusIds.length > 0) {
                for (const redPlusId of customerConfig.redplusIds) {
                    redPlusItems.push(createBasketItem({ sublevelTariffId: redPlusId }));
                }
            }

            const basketItem = createBasketItem({
                sublevelTariffId: currentOffer!.subLevelId,
                ...customerConfig.atomicId && { atomicDeviceId: customerConfig.atomicId },
                discountIds: getAllDiscounts({
                    deviceId: customerConfig.deviceId || null,
                    isGigakombi: customerConfig.hasGigakombi,
                    isRestlaufzeit: isEligibleForRestlaufzeit && customerConfig.isRestlaufzeit,
                    isTauschbonus: customerConfig.hasTauschbonus,
                    isTradeIn: customerConfig.hasTradeIn,
                    salesChannel: customerConfig.salesChannel!,
                    subscriptionIds: [customerConfig.selectedSubscriptionId!],
                }),
                serviceIds,
                redPlusItems,
            });

            const proposition = createBasketProposition(
                customerConfig.salesChannel === SALESCHANNEL_REDPLUS ? BASKET_PROPOSITION_REDPLUS : BASKET_PROPOSITION_BNT,
                [basketItem],
            );

            const basket = createBasket(customerConfig.salesChannel!, [proposition]);

            const deeplink = generateDeeplink(basket, true);

            window.location.assign(deeplink);
        },
    }),
    /**
     * Trigger getData action for user interactions that require reloading the API information
     * Currently these are
     * - `SET_RLZ`
     *
     * `SET_RLZ` alters the API request by adding or removing a discount soc for Restlaufzeit, but that actually
     * doesn't change the API response as of today (2024-06-21).
     */
    setRestlaufzeit: () => startAppListening({
        actionCreator: setRestlaufzeit,
        effect: (_, api) => {
            api.dispatch(getData());
        },
    }),
    toggleDevice: () => startAppListening({
        actionCreator: toggleDevice,
        effect: (_, api) => {
            const customerConfig = selectCustomerConfig(api.getState());

            if (hasQueryParam(URL_PARAM_ATOMIC_ID) && !customerConfig.atomicId) {
                updateUrl(removeQueryParam(URL_PARAM_ATOMIC_ID), true);
            }
        },
    }),
    setSubscription: () => startAppListening({
        actionCreator: setSubscription,
        effect: () => {
            if (hasQueryParam(URL_PARAM_SUBGROUP_ID)) {
                updateUrl(removeQueryParam(URL_PARAM_SUBGROUP_ID), true);
            }

            // DO NOT EVER add page view tracking to this listener.
            // It's triggered not only on page load but on several toggling actions as well
            // which are not supposed to trigger a page view.
            // The listener 'currentOfferChanged' handles tracking.
        },
    }),
    /**
     * Track page view after active offer changed (e.g. customer selected a new tariff)
     * This action is not triggered when the previous current offer is `null`, to prevent tracking a RLZ change which
     * removes and resets the current offer.
     */
    currentOfferChanged: () => startAppListening({
        predicate: (action, currentState, previousState) => {
            const currentOfferData = selectCurrentOfferData(currentState);
            const previousOfferData = selectCurrentOfferData(previousState);

            const {
                defaultSubscriptionId: currentDefaultSubscriptionId,
                selectedSubscriptionId: currentSelectedSubscriptionId,
            } = selectCustomerConfig(currentState);
            const {
                defaultSubscriptionId: previousDefaultSubscriptionId,
                selectedSubscriptionId: previousSelectedSubscriptionId,
            } = selectCustomerConfig(previousState);

            const isDeviceFirst = previousDefaultSubscriptionId && !previousSelectedSubscriptionId;
            const isCheapestSameAsDefault = currentDefaultSubscriptionId === currentSelectedSubscriptionId;

            // Page view tracking should happen if:
            //     - we are in device first flow AND the cheapest tariff for the device is the same as the default tariff from CMS (INCM-4515)
            //     - regardless which flow the current offer is different from the previous offer (INCM-4163)
            //     - for BOTH cases above only if this listener was not triggered by a RLZ toggle
            return (
                (isDeviceFirst && isCheapestSameAsDefault)
                    || !isShallowEqual(currentOfferData, previousOfferData)
            )
                && !!previousOfferData
                && !setRestlaufzeit.match(action);
        },
        effect: (_, { getState }) => {
            const trackingData = selectTrackingPayload(getState());
            const trackingPayload = createPageViewTrackingData(trackingData);

            trackingPayload && trackIt(TrackType.PageView, trackingPayload);
        },
    }),
};

/**
 * Starts all listeners.
 * Returns a function to unsubscribe all listeners.
 */
export const startListeners = () => {
    const unsubscribeListeners = Object.values(listeners).map(listener => listener());

    return () => {
        unsubscribeListeners.forEach(unsubscribeListener => unsubscribeListener());
    };
};
